/**
 * Locale-aware display formatters. The product reports in GBP only, so the
 * currency formatter is fixed to en-GB / £.
 */

const currencyFormatter = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const compactCurrencyFormatter = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat('en-GB');

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const dateTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** £1,234.56 — em-dash for missing values. */
export function formatCurrency(value: number | null | undefined, currency = 'GBP'): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }

  return (
    currency === 'GBP'
      ? currencyFormatter
      : new Intl.NumberFormat('en-GB', { style: 'currency', currency })
  ).format(value);
}

/** £1,235 — whole pounds for stat-card numerals. */
export function formatCurrencyWhole(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }

  return compactCurrencyFormatter.format(value);
}

/** 1,234 with grouping. */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }

  return numberFormatter.format(value);
}

/** "25 May 2026" from an ISO timestamp or YYYY-MM-DD string. */
export function formatDate(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return '—';
  }

  return dateFormatter.format(parsed);
}

/** "25 May 2026, 14:32" — distinguishes same-day rows (e.g. duplicate uploads). */
export function formatDateTime(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return '—';
  }

  return dateTimeFormatter.format(parsed);
}

/** "12.5%" — trims trailing zeros. */
export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }

  const rounded = Math.round(value * 100) / 100;
  return `${numberFormatter.format(rounded)}%`;
}

const oneDecimalFormatter = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 });

/**
 * "742 KB" / "2.4 MB" — whole numbers below 1 MB (a small file misreported
 * in MB reads as "0.7 MB"), one decimal place from MB upward.
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) {
    return '—';
  }

  if (bytes < 1024) {
    return `${numberFormatter.format(Math.round(bytes))} B`;
  }

  if (bytes < 1024 ** 2) {
    return `${numberFormatter.format(Math.round(bytes / 1024))} KB`;
  }

  if (bytes < 1024 ** 3) {
    return `${oneDecimalFormatter.format(bytes / 1024 ** 2)} MB`;
  }

  return `${oneDecimalFormatter.format(bytes / 1024 ** 3)} GB`;
}
